/**
 * pdf.js — generate a customer receipt as a PDF on the cashier's phone.
 *
 * Layout mirrors the desktop's thermal-printer receipt (see desktop
 * src/main/ipc/printer.js > buildReceiptHTML for the original template).
 * The cashier's phone is the only device that has the receipt during an
 * outage, so the customer needs to see something familiar.
 *
 * 80mm-roll equivalent — same width the desktop targets. Auto-shares via
 * navigator.share() on phones; falls back to opening the PDF in a new tab
 * (or saving it via the OS) on desktop browsers.
 */
import jsPDF from 'jspdf'
import { db } from './db'

// 80mm thermal roll geometry, matching the desktop:
//   page = 80mm wide, body = 72mm (4mm side padding)
const PAGE_WIDTH_MM = 80
const SIDE_PADDING_MM = 4
const TABLE_NAME_W = 38   // mm  (Item column)
const TABLE_QTY_W  = 8    // mm  (Qty column, centred)
const TABLE_PRICE_W = 12  // mm  (Price column, right)
const LINE_H = 4          // mm  (default line-height)

const money = (v) => {
  const n = Number(v)
  return (Number.isFinite(n) ? n : 0).toFixed(2)
}

/**
 * Generate the receipt PDF as a Blob, persist it to IndexedDB (so the
 * cashier can re-share later), and return it for immediate share.
 *
 * The shape of `payload` is what the outbox stored — i.e. exactly what the
 * PWA POSTs to /api/portal/pos-submit. `pharmacyName` comes from the login
 * response and is cached in db.meta.
 */
export async function generateReceipt({ payload, pharmacyName, pharmacyAddress, pharmacyPhone, footer }) {
  // Start tall enough; we'll trim if needed. jsPDF needs an upfront height,
  // and the cost of "too tall" is just blank PDF tail — invisible when shared.
  const initialH = estimateHeight(payload)
  const doc = new jsPDF({ unit: 'mm', format: [PAGE_WIDTH_MM, initialH], compress: true })

  // Monospaced font keeps columns aligned like the desktop's "Courier New" template.
  doc.setFont('courier', 'normal')
  doc.setFontSize(8.5)

  let y = 6

  const center = (text, opts = {}) => {
    const { size, bold } = opts
    if (size) doc.setFontSize(size)
    if (bold) doc.setFont('courier', 'bold')
    const w = doc.getTextWidth(String(text))
    doc.text(String(text), (PAGE_WIDTH_MM - w) / 2, y)
    y += LINE_H
    if (size) doc.setFontSize(8.5)
    if (bold) doc.setFont('courier', 'normal')
  }
  const left = (text) => { doc.text(String(text), SIDE_PADDING_MM, y); y += LINE_H }
  const between = (l, r, opts = {}) => {
    const { bold, size } = opts
    if (size) doc.setFontSize(size)
    if (bold) doc.setFont('courier', 'bold')
    doc.text(String(l), SIDE_PADDING_MM, y)
    const rw = doc.getTextWidth(String(r))
    doc.text(String(r), PAGE_WIDTH_MM - SIDE_PADDING_MM - rw, y)
    y += LINE_H
    if (bold) doc.setFont('courier', 'normal')
    if (size) doc.setFontSize(8.5)
  }
  // Dashed horizontal rule — matches the desktop's dashed <hr>.
  const rule = () => {
    doc.setLineDashPattern([0.6, 0.6], 0)
    doc.setLineWidth(0.2)
    doc.line(SIDE_PADDING_MM, y - 1.2, PAGE_WIDTH_MM - SIDE_PADDING_MM, y - 1.2)
    doc.setLineDashPattern([], 0)
    y += 1
  }

  // ── Header (centered) ─────────────────────────────────────────────────────
  center(pharmacyName || 'PharmaCare Pharmacy', { size: 11, bold: true })
  if (pharmacyAddress) center(pharmacyAddress, { size: 8 })
  if (pharmacyPhone)   center(`Tel: ${pharmacyPhone}`, { size: 8 })
  rule()

  // ── Receipt meta ──────────────────────────────────────────────────────────
  const receiptRef = `PWA-${String(payload.client_op_id).slice(0, 8).toUpperCase()}`
  left(`Receipt #: ${receiptRef}`)
  left(`Date: ${formatDate(payload.occurred_at)}`)
  if (payload.cashier_name) left(`Cashier: ${payload.cashier_name}`)
  rule()

  // ── Item table header ─────────────────────────────────────────────────────
  drawTableRow(doc, y, 'Item', 'Qty', 'Price', 'Total', { bold: true })
  y += LINE_H
  doc.setLineDashPattern([], 0)
  doc.setLineWidth(0.3)
  doc.line(SIDE_PADDING_MM, y - 1.2, PAGE_WIDTH_MM - SIDE_PADDING_MM, y - 1.2)
  y += 1

  // ── Item rows ─────────────────────────────────────────────────────────────
  for (const item of payload.items) {
    const qty = Number(item.quantity) || 0
    const unitPrice = Number(item.unit_price) || 0
    const lineTotal = Number(item.line_total) || qty * unitPrice
    // Name precedence matches the desktop receipt (printer.js buildReceiptHTML).
    const name = item.generic_name || item.brand_name || item.drug_name || 'Unknown'

    // Wrap long item names to multiple lines using only the Item column.
    const wrappedName = doc.splitTextToSize(name, TABLE_NAME_W - 1)
    if (wrappedName.length <= 1) {
      drawTableRow(doc, y, wrappedName[0] || name, String(qty), money(unitPrice), money(lineTotal))
      y += LINE_H
    } else {
      // First wrapped line gets the numbers; subsequent lines just continue the name.
      drawTableRow(doc, y, wrappedName[0], String(qty), money(unitPrice), money(lineTotal))
      y += LINE_H
      for (let i = 1; i < wrappedName.length; i++) {
        drawTableRow(doc, y, wrappedName[i], '', '', '')
        y += LINE_H
      }
    }

    // Sub-line: dosage_form + strength — identical to the desktop receipt.
    const sub = [item.dosage_form, item.strength].filter(Boolean).join(' ').trim()
    if (sub) {
      doc.setFontSize(7)
      doc.setTextColor(80, 80, 80)
      doc.text(`  ${sub}`, SIDE_PADDING_MM, y)
      y += LINE_H - 0.5
      doc.setFontSize(8.5)
      doc.setTextColor(0, 0, 0)
    }
  }
  rule()

  // ── Totals block ──────────────────────────────────────────────────────────
  between('Subtotal', `GHS ${money(payload.subtotal)}`)
  if (Number(payload.discount_amount) > 0) {
    between('Discount', `-GHS ${money(payload.discount_amount)}`)
  }
  between('TOTAL', `GHS ${money(payload.total_amount)}`, { bold: true, size: 10 })
  between(`Paid (${payload.payment_mode || 'CASH'})`, `GHS ${money(payload.amount_paid)}`)
  const change = Math.max(0, Number(payload.amount_paid) - Number(payload.total_amount))
  between('Change', `GHS ${money(change)}`)
  rule()

  // ── Footer ────────────────────────────────────────────────────────────────
  center(footer || 'Thank you! Get well soon.', { size: 8 })
  y += 1

  const blob = doc.output('blob')
  // Persist for the Receipts history. Status copy is what gets shown there.
  await db.receipts.put({
    client_op_id: payload.client_op_id,
    pdf_blob: blob,
    occurred_at: payload.occurred_at,
    total_amount: Number(payload.total_amount) || 0,
    payment_mode: payload.payment_mode,
    item_count: payload.items.length,
    item_summary: payload.items.slice(0, 3).map(i => i.brand_name || i.generic_name).filter(Boolean).join(', ')
                  + (payload.items.length > 3 ? ` + ${payload.items.length - 3} more` : ''),
    receipt_ref: receiptRef,
  })
  return blob
}

function drawTableRow(doc, y, name, qty, price, total, opts = {}) {
  if (opts.bold) doc.setFont('courier', 'bold')
  // Item column — left-aligned.
  doc.text(String(name), SIDE_PADDING_MM, y)
  // Qty column — centred.
  const qtyText = String(qty)
  const qtyX = SIDE_PADDING_MM + TABLE_NAME_W + (TABLE_QTY_W - doc.getTextWidth(qtyText)) / 2
  doc.text(qtyText, qtyX, y)
  // Price column — right-aligned.
  const priceText = String(price)
  const priceX = SIDE_PADDING_MM + TABLE_NAME_W + TABLE_QTY_W + TABLE_PRICE_W - doc.getTextWidth(priceText)
  doc.text(priceText, priceX, y)
  // Total column — right-aligned.
  const totalText = String(total)
  const totalX = PAGE_WIDTH_MM - SIDE_PADDING_MM - doc.getTextWidth(totalText)
  doc.text(totalText, totalX, y)
  if (opts.bold) doc.setFont('courier', 'normal')
}

// Estimate the page height we need so jsPDF doesn't truncate. We over-provision
// a bit — the wasted tail is invisible when sharing the PDF.
function estimateHeight(payload) {
  const headerLines = 5   // pharmacy name + 2 optional meta + spacing
  const metaLines = 3     // receipt + date + cashier
  const tableHeader = 2
  const itemLines = (payload.items || []).reduce((s, it) => {
    const sub = (it.dosage_form || it.strength) ? 1 : 0
    // Worst case: brand name wraps to 2 lines.
    return s + 2 + sub
  }, 0)
  const totalsLines = 5
  const footerLines = 3
  return Math.max(80, (headerLines + metaLines + tableHeader + itemLines + totalsLines + footerLines) * LINE_H + 30)
}

function formatDate(iso) {
  // Match the desktop receipt's date formatting (printer.js uses toLocaleString()).
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return String(iso)
  }
}

export async function shareReceipt(blob, clientOpId) {
  const filename = `receipt-${String(clientOpId).slice(0, 8)}.pdf`
  const file = new File([blob], filename, { type: 'application/pdf' })

  // Modern phones: native share sheet (WhatsApp, AirDrop, etc.).
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Receipt' })
      return { method: 'share' }
    } catch (err) {
      if (err?.name === 'AbortError') return { method: 'cancelled' }
      // Fall through to open-in-tab below.
    }
  }

  // Fallback (desktop browsers / iOS Safari pre-15.4): open in a new tab so
  // the user can save / send manually.
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return { method: 'open' }
}
